import { cloneDeep, isNumber, isUndefined } from 'lodash';
import { v4 as uuid } from 'uuid';
import { combineOriginIndex, isLayerProps, parseLayerId, trackConfig } from '@/framework';
import { DefaultClientImageLayer } from '@/version-parse';
import {
    IBackgroundLayerProps,
    IExportPlugin,
    ILayerProps,
    IPropsData,
    LayerType,
    MaterialIdsType,
    PluginType,
    StretchOptionType,
} from '@/version-types';
import { Layer } from '../layer';
import { BackgroundHistory } from './background-history';
import backgroundLayerBehavior from './behavior';
import backgroundLayerState, { getBackgroundLayerState } from './states/background-state';
import { HistoryType, IBackgroundLayerUpdateProps } from './type';
import { createBackgroundLayer } from './utils';

export class BackgroundLayer extends Layer {
    /**
     * 获取背景图层
     *
     * @param stageIndex - 舞台索引
     */
    getBackgroundLayer(stageIndex?: number) {
        const originIndex = !isUndefined(stageIndex)
            ? this.framework.convertStageToOrigin(stageIndex)
            : this.framework.stage.getActiveOriginIndex();
        return this.framework.getBackgroundLayer(originIndex);
    }

    /**
     * 更改图层属性
     *
     * @param layer - 原图层
     * @param data - 修改的属性
     * @param expand - 可选；扩展属性
     */
    generateLayer(layer: ILayerProps, data: IPropsData, expand = {}) {
        layer = cloneDeep(layer);
        layer.data = data;
        layer = { ...layer, ...expand };
        return layer;
    }

    /**
     * 更新图层写入历史记录
     *
     * @param layer - 新图层
     * @param oldLayer - 可选；旧图层
     * @param trackFunc - 可选；功能
     */
    updateAndWrite(layer: ILayerProps, oldLayer?: ILayerProps, trackFunc?: string) {
        this.framework.stage.addOrUpdate(layer);
        if (oldLayer) {
            this.framework.history.write(oldLayer, layer, trackFunc);
        }
        this.framework.operate.delActiveLayer();
    }

    /**
     * 恢复原始数据
     *
     * @param oldLayer - 旧图层
     * @param layer - 新图层
     */
    resetData(oldLayer: ILayerProps, layer: ILayerProps) {
        this.update(layer, true, trackConfig[LayerType.BACKGROUND_LAYER]?.reset, oldLayer);
    }

    update(
        options: IBackgroundLayerUpdateProps | ILayerProps,
        isPushHistory: boolean = true,
        trackFunc?: string,
        oldLayer?: ILayerProps,
        ignoreBatchUpdate = false,
        index?: number,
    ) {
        const stageSelection = this.framework.stage.getStageSelection();
        const originIndex = this.framework.stage.getActiveOriginIndex();
        const layer = oldLayer || this.framework.getBackgroundLayer(index !== undefined ? index : originIndex);
        if (layer) {
            let newLayer: ILayerProps;
            if (isLayerProps(options)) {
                newLayer = cloneDeep(options);
            } else {
                const materialIds: Record<string, string[]> = {};
                const data = layer.data as IBackgroundLayerProps;
                const {
                    id = '',
                    backgroundColor,
                    url,
                    backgroundGradient = null,
                    naturalWidth = data.naturalWidth,
                    naturalHeight = data.naturalHeight,
                    imageTransform = {
                        offsetX: 0,
                        offsetY: 0,
                        rotate: 0,
                        scale: 1,
                        flipH: false,
                        flipV: false,
                        widthRatio: 1,
                        heightRatio: 1,
                    },
                    opacity = 1,
                    stretchOption = StretchOptionType.cover,
                    isAILayer = false,
                    dynamicInfo,
                } = options;
                if (id) {
                    materialIds[MaterialIdsType.BACKGROUND] = [String(id)];
                }

                newLayer = this.generateLayer(
                    layer,
                    {
                        ...layer.data,
                        backgroundColor,
                        backgroundGradient: backgroundGradient || null,
                        url,
                        stretchOption,
                        naturalWidth,
                        naturalHeight,
                        imageTransform,
                        dynamicInfo,
                    },
                    { materialIds, opacity, isAILayer },
                );
            }

            if (!ignoreBatchUpdate && stageSelection.length > 1) {
                this.batchUpdateBackground(newLayer, stageSelection, isPushHistory, trackFunc, oldLayer);
            } else {
                this.updateAndWrite(newLayer, isPushHistory ? layer : undefined, trackFunc);
            }
        }
    }

    /**
     * 保存原始数据
     *
     * @param layer - 图层属性
     */
    setOriginBackground(layer: ILayerProps) {
        const { setOriginBackground } = getBackgroundLayerState(this.identity);
        setOriginBackground(layer);
    }

    /**
     * 获取原始数据
     *
     * @param id - 图层 ID
     */
    getOriginBackground(id: string): ILayerProps | undefined {
        const { getOriginBackground } = getBackgroundLayerState(this.identity);
        return getOriginBackground(id);
    }

    /**
     * 销毁背景模块全局store
     */
    destroy() {
        const { destroy } = getBackgroundLayerState(this.identity);
        destroy();
    }

    /**
     * 创建空白背景
     *
     * @param options - 背景图层创建选项
     */
    create = createBackgroundLayer;

    /**
     * 获取背景素材 ID
     *
     * @param layer - 图层属性
     */
    getBackgroundMaterialId(layer: ILayerProps | null) {
        if (layer) {
            const { materialIds } = layer;
            const ids = materialIds?.[MaterialIdsType.BACKGROUND] || [0];
            return ids[0];
        }
        return 0;
    }

    /**
     * 获取背景颜色
     */
    getBackgroundColor() {
        const originIndex = this.framework.stage.getActiveOriginIndex();
        const layer = this.framework.getBackgroundLayer(originIndex);
        if (layer) {
            const { backgroundColor } = layer.data as IBackgroundLayerProps;
            return backgroundColor;
        }
        return '';
    }

    /**
     * 垂直翻转
     */
    setVertical() {
        const originIndex = this.framework.stage.getActiveOriginIndex();
        const layer = this.framework.getBackgroundLayer(originIndex);
        if (!layer) return;
        const backgroundData = layer.data as IBackgroundLayerProps;
        const { imageTransform } = backgroundData;

        this.update(
            {
                url: backgroundData.url,
                backgroundColor: backgroundData.backgroundColor,
                imageTransform: { ...imageTransform, flipV: !imageTransform.flipV },
                opacity: layer.opacity,
            },
            true,
            trackConfig[LayerType.BACKGROUND_LAYER]?.flipV,
            layer,
        );
    }

    /**
     * 水平翻转
     */
    setHorizontal() {
        const originIndex = this.framework.stage.getActiveOriginIndex();
        const layer = this.framework.getBackgroundLayer(originIndex);
        if (!layer) return;
        const backgroundData = layer.data as IBackgroundLayerProps;
        const { imageTransform } = backgroundData;

        this.update(
            {
                url: backgroundData.url,
                backgroundColor: backgroundData.backgroundColor,
                imageTransform: {
                    ...imageTransform,
                    flipH: !imageTransform.flipH,
                },
                opacity: layer.opacity,
            },
            true,
            trackConfig[LayerType.BACKGROUND_LAYER]?.flipH,
            layer,
        );
    }

    /**
     * 更新渐变色
     *
     * @param options - 背景图层更新选项
     * @param isPushHistory - 可选；是否添加到历史记录；默认值为 `true`
     * @param oldLayer - 旧图层属性
     */
    setBackgroundGradient(options: IBackgroundLayerUpdateProps, isPushHistory: boolean = true, oldLayer?: ILayerProps) {
        this.update(options, isPushHistory, trackConfig[LayerType.BACKGROUND_LAYER]?.gradient, oldLayer);
    }

    /**
     * 更新背景颜色
     *
     * @param options - 背景图层更新选项
     * @param isPushHistory - 可选；是否添加到历史记录；默认值为 `true`
     * @param oldLayer - 旧图层属性
     */
    setBackgroundColor(options: IBackgroundLayerUpdateProps, isPushHistory: boolean = true, oldLayer?: ILayerProps) {
        this.update(options, isPushHistory, trackConfig[LayerType.BACKGROUND_LAYER]?.color, oldLayer);
    }

    /**
     * 更新背景图
     *
     * @param options - 背景图层更新选项
     * @param isPushHistory - 可选；是否添加到历史记录；默认值为 `true`
     */
    setBackgroundUrl(options: IBackgroundLayerUpdateProps, isPushHistory: boolean = true) {
        this.update(options, isPushHistory, trackConfig[LayerType.BACKGROUND_LAYER]?.url);
    }

    /**
     * 恢复原始比例
     *
     * @param options - 背景图层更新选项
     * @param isPushHistory - 可选；是否添加到历史记录；默认值为 `true`
     */
    setOriginRatio(options: IBackgroundLayerUpdateProps, isPushHistory: boolean = true) {
        this.update(options, isPushHistory, trackConfig[LayerType.BACKGROUND_LAYER]?.reset);
    }

    /**
     * 从列表添加背景图
     *
     * @param options - 背景图层更新选项
     * @param isPushHistory - 可选；是否添加到历史记录；默认值为 `true`
     */
    addBackgroundUrl(
        options: IBackgroundLayerUpdateProps,
        isPushHistory: boolean = true,
        trackFunc?: string,
        index?: number,
    ) {
        this.update(
            options,
            isPushHistory,
            trackFunc ?? trackConfig[LayerType.BACKGROUND_LAYER]?.add,
            undefined,
            undefined,
            index,
        );
    }

    /**
     * 背景转图片
     */
    async convertBackgroundToImage() {
        const originIndex = this.framework.stage.getActiveOriginIndex();
        const backgroundLayer = this.framework.getBackgroundLayer(originIndex);
        if (!backgroundLayer) return;
        const newImageLayer = cloneDeep(DefaultClientImageLayer);
        const oldBgLayer = cloneDeep(backgroundLayer);
        const data = backgroundLayer.data as IBackgroundLayerProps;

        newImageLayer.data = {
            ...newImageLayer.data,
            ...cloneDeep(data),
            stretchOption: StretchOptionType.fill,
        };

        oldBgLayer.data = newImageLayer.data;
        Object.assign(newImageLayer, oldBgLayer); // 拷贝 data 以外的属性

        newImageLayer.layerType = LayerType.IMAGE_LAYER;
        newImageLayer.id = combineOriginIndex(uuid(), originIndex);
        const bgOptions = {
            url: '',
            backgroundColor: '#ffffffff',
        };

        this.update(bgOptions, false, undefined, undefined, true);
        const info = this.framework.tryGetOriginIdsAndIndex(originIndex, true);
        const originIds = info.originIds;
        originIds.splice(1, 0, newImageLayer.id);
        this.framework.stage.addOrUpdate(newImageLayer, true, originIndex);
        this.framework.operate.setActiveLayer(newImageLayer.id);

        this.framework.history.write({
            type: HistoryType.CONVERT_BACKGROUND_TO_IMAGE,
            data: {
                /** backgroundLayer 原先的背景图层 */
                backgroundLayer,
                /** imageLayer 新的图片图层 */
                imageLayer: newImageLayer,
                /** bgOptions 背景图层更新选项 */
                bgOptions,
            },
        });
    }

    /**
     * 批量更新背景
     *
     * @param options - 背景图层更新选项
     * @param isPushHistory - 可选；是否添加到历史记录；默认值为 `true`
     */
    batchUpdateBackground(
        layer: ILayerProps,
        stageSelection: number[],
        isPushHistory?: boolean,
        trackFunc?: string,
        oldLayer?: ILayerProps,
    ) {
        let oldLayers: ILayerProps[] = [];
        if (isPushHistory) {
            oldLayers = stageSelection
                .map((index) => this.framework.getBackgroundLayer(this.framework.convertStageToOrigin(index)))
                .filter((oldLayer) => !!oldLayer) as ILayerProps[];
            if (oldLayer) {
                const { originIndex } = parseLayerId(oldLayer?.id);
                if (isNumber(originIndex)) {
                    const stageIndex = this.framework.convertOriginToStage(originIndex);
                    oldLayers.splice(stageIndex, 0, oldLayer);
                }
            }
        }
        const newLayers = oldLayers
            ?.map((oldLayer) => {
                if (oldLayer) {
                    const newLayer = cloneDeep(layer);
                    newLayer.id = oldLayer.id;
                    return newLayer;
                }
            })
            .filter((newLayer) => !!newLayer) as ILayerProps[];

        this.framework.stage.batchUpdate(newLayers, true);

        if (isPushHistory && oldLayers.length) {
            this.framework.history.write({
                type: HistoryType.BATCH_UPDATE_BACKGROUND,
                data: {
                    newLayers,
                    oldLayers,
                    stageSelection,
                },
                trackFunc,
            });
        }
        this.framework.operate.delActiveLayer();
    }
}

/**
 * 背景图层插件
 */
export const backgroundLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.BACKGROUND_LAYER,
    createInstance({ identity }) {
        return [{ key: 'BackgroundLayer', value: new BackgroundLayer(identity) }];
    },
    createState() {
        return [backgroundLayerState];
    },
    createBehavior() {
        return backgroundLayerBehavior;
    },
    createHistory({ identity }) {
        return [new BackgroundHistory(identity)];
    },
    destroy({ getInstance }) {
        getInstance(BackgroundLayer).destroy();
    },
};
